// NOT-36 — shared resolution of a stored `businessUnitId` to a human label.
//
// The pricebook item form stores a Business Unit id (a UUID). Every surface that
// displays that value must resolve it back to the BU name rather than show a raw
// UUID. This is the single source of that mapping so the table, CSV export and
// detail view all agree. Greenfield: `businessUnitId` is always either a valid
// BU id or null — there is no legacy free-text case to handle.
import { useCallback, useMemo } from 'react';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';

/**
 * Resolve a stored `businessUnitId` to a display label.
 *   - empty / null          → null (caller renders a dash / omits the field)
 *   - matches a known BU id  → that BU's name
 *   - no match               → null (never show a bare id to users)
 */
export function resolveBusinessUnitLabel(
  value: string | null | undefined,
  unitsById: ReadonlyMap<string, string>,
): string | null {
  if (!value) return null;
  return unitsById.get(value) ?? null;
}

/**
 * Hook form of {@link resolveBusinessUnitLabel}. Reads every business unit
 * (`status: 'all'` so archived units still resolve to a name) and returns a
 * memoised `labelFor` resolver plus the query's loading flag.
 */
export function useBusinessUnitLabel() {
  const { data: units = [], isLoading } = useBusinessUnits('all');
  const unitsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const unit of units) map.set(unit.id, unit.name);
    return map;
  }, [units]);
  const labelFor = useCallback(
    (value: string | null | undefined) => resolveBusinessUnitLabel(value, unitsById),
    [unitsById],
  );
  return { labelFor, isLoading };
}
